import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, LoaderCircle, Sparkles, X, ArrowLeft } from "lucide-react";
import { Wordmark } from "./Nav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuccessView = () => {
  const [state, setState] = useState("checking");

  useEffect(() => {
    const sessionId = new URLSearchParams(window.location.search).get("session_id");
    if (!sessionId) { setState("error"); return; }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/payments/status/${sessionId}`);
        const data = await res.json();
        if (data.status === "completed" || data.payment_status === "paid") {
          setState("done");
          return;
        }
      } catch {}
      attempts += 1;
      if (attempts < 12) setTimeout(poll, 2000);
      else setState("done");
    };
    poll();
  }, []);

  return (
    <div className="text-center max-w-lg" data-testid="payment-success-page">
      {state === "checking" ? (
        <>
          <LoaderCircle className="w-12 h-12 text-diem-blue animate-spin mx-auto" />
          <h1 className="mt-8 font-display text-4xl font-medium tracking-tighter text-diem-ink">Confirming your subscription…</h1>
          <p className="mt-4 text-diem-slate">One moment while Stripe confirms everything.</p>
        </>
      ) : (
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          <span className="w-16 h-16 rounded-full bg-diem-ok flex items-center justify-center mx-auto shadow-[0_16px_40px_rgba(16,185,129,0.4)]">
            <Check className="w-8 h-8 text-white" />
          </span>
          <h1 className="mt-8 font-display text-4xl sm:text-5xl font-medium tracking-tighter text-diem-ink">
            Welcome, <span className="text-diem-blue">Founding Member.</span>
          </h1>
          <p className="mt-5 text-lg text-diem-slate leading-relaxed">
            Your Project Owner subscription is active — with your first 30 days free. You'll be first
            through the door when Deyem launches on iPhone and Android.
          </p>
          <p className="mt-4 inline-flex items-center gap-2 rounded-full bg-diem-blue/10 text-diem-blue text-sm font-bold px-4 py-2">
            <Sparkles className="w-4 h-4" /> Founding Member status locked in
          </p>
          <div className="mt-10">
            <a href="/" data-testid="payment-success-home-link"
              className="inline-flex items-center gap-2 rounded-full bg-diem-blue text-white font-semibold px-7 py-3.5 shadow-[0_10px_30px_rgba(0,85,255,0.4)] hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300">
              <ArrowLeft className="w-4 h-4" /> Back to deyem.co.uk
            </a>
          </div>
        </motion.div>
      )}
    </div>
  );
};

const CancelView = () => (
  <div className="text-center max-w-lg" data-testid="payment-cancel-page">
    <span className="w-16 h-16 rounded-full bg-diem-mist flex items-center justify-center mx-auto">
      <X className="w-7 h-7 text-diem-muted" />
    </span>
    <h1 className="mt-8 font-display text-4xl font-medium tracking-tighter text-diem-ink">No charge made.</h1>
    <p className="mt-5 text-lg text-diem-slate leading-relaxed">
      You cancelled checkout — nothing was taken. The 30-day free trial will be waiting whenever you're ready.
    </p>
    <div className="mt-10 flex justify-center gap-4">
      <a href="/#pricing" data-testid="payment-cancel-pricing-link"
        className="inline-flex items-center gap-2 rounded-full bg-diem-blue text-white font-semibold px-7 py-3.5 shadow-[0_10px_30px_rgba(0,85,255,0.4)] hover:-translate-y-0.5 transition-[box-shadow,transform] duration-300">
        Back to pricing
      </a>
      <a href="/" data-testid="payment-cancel-home-link"
        className="inline-flex items-center gap-2 rounded-full bg-white border border-diem-line text-diem-ink font-semibold px-7 py-3.5 hover:bg-diem-paper transition-colors duration-300">
        Home
      </a>
    </div>
  </div>
);

export const PaymentResult = ({ kind }) => (
  <div className="min-h-screen bg-diem-paper hero-glow flex flex-col" data-testid="payment-result-page">
    <header className="px-6 lg:px-8 h-[72px] flex items-center max-w-7xl mx-auto w-full">
      <a href="/" aria-label="Deyem home"><Wordmark /></a>
    </header>
    <main className="flex-1 flex items-center justify-center px-6 py-16">
      {kind === "success" ? <SuccessView /> : <CancelView />}
    </main>
  </div>
);
